// Bouton + boîte de dialogue pour envoyer une facture client par email (PDF joint).
// Pré-remplit l'email depuis la fiche tiers ; affiche l'historique d'envoi.
import React, { useState } from "react";
import api, { formatErreur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Loader2, Send, CheckCircle2 } from "lucide-react";

const fmtDateHeure = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR",
      { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export function EnvoiFactureBouton({ factureId, numero, testId, onSent }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [envois, setEnvois] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  const ouvrir = async () => {
    setOpen(true);
    setChargement(true);
    try {
      const { data } = await api.get(`/factures/${factureId}`);
      setEmail(data.tiers_email || "");
      setEnvois(data.envois || []);
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setChargement(false);
    }
  };

  const envoyer = async () => {
    if (!email.trim()) {
      toast.error("Veuillez renseigner l'adresse email du client.");
      return;
    }
    setEnvoiEnCours(true);
    try {
      const { data } = await api.post(`/factures/${factureId}/envoyer-email`, { email: email.trim() });
      toast.success(`Facture envoyée à ${data.email} le ${fmtDateHeure(data.date)}`);
      setEnvois((prev) => [...prev, { email: data.email, date: data.date }]);
      if (onSent) onSent();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "L'envoi a échoué.");
    } finally {
      setEnvoiEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? ouvrir() : setOpen(false))}>
      <DialogTrigger asChild>
        <button type="button" data-testid={testId || `envoyer-facture-${numero || factureId}`}
          title="Envoyer par email"
          className="h-7 w-7 rounded-md flex items-center justify-center text-[#1D3D3D] hover:bg-[#E8F4F4] transition-colors">
          <Mail className="h-3.5 w-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent data-testid="dialog-envoi-facture">
        <DialogHeader>
          <DialogTitle>Envoyer la facture par email</DialogTitle>
          <DialogDescription>
            {numero ? `Facture ${numero}` : "Facture"} — le PDF sera joint à l'email.
            Une copie est adressée à votre entreprise.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-[#1D3D3D]">Email du client</Label>
            <Input data-testid="envoi-email-input" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="client@exemple.pf"
              className="bg-white border-[#4A8A8A]/30" />
            {!chargement && !email && (
              <p className="text-xs text-amber-600">Aucun email dans la fiche tiers : saisissez-le ci-dessus.</p>
            )}
          </div>
          {envois.length > 0 && (
            <div className="rounded-lg bg-[#E8F4F4]/60 p-3" data-testid="envoi-historique">
              <p className="text-xs font-bold text-[#1D3D3D] mb-1">Historique d'envoi</p>
              <ul className="space-y-1">
                {envois.map((e, i) => (
                  <li key={i} className="text-xs text-[#1D3D3D]/80 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> {e.email} — {fmtDateHeure(e.date)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="envoi-confirmer" onClick={envoyer} disabled={envoiEnCours || !email.trim()}
            className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
            {envoiEnCours ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EnvoiFactureBouton;
